/*
    Copyright NetFoundry Inc.

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    https://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import {ExtendableComponent} from "../extendable/extendable.component";
import {Component, DoCheck, ElementRef, EventEmitter, HostListener, Inject, Input, OnDestroy, OnInit, Output, ViewChild} from "@angular/core";

import {defer, isEqual, unset, debounce, cloneDeep, forEach, isArray, isEmpty, isObject, isNil, map, omitBy, slice, get} from "lodash";
import {GrowlerModel} from "../messaging/growler.model";
import {GrowlerService} from "../messaging/growler.service";
import {ExtensionService, SHAREDZ_EXTENSION} from "../extendable/extensions-noop.service";
import {Identity} from "../../models/identity";
import {ZITI_DATA_SERVICE, ZitiDataService} from "../../services/ziti-data.service";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {Subscription} from "rxjs";
import {Location} from "@angular/common";
import {SETTINGS_SERVICE, SettingsService} from "../../services/settings.service";
import {URLS} from "../../urls";
import {MatDialog} from "@angular/material/dialog";
import {ConfirmComponent} from "../confirm/confirm.component";

export class Entity {
    name: ''
}

export const KEY_CODES = {
    LEFT_ARROW: 37,
    UP_ARROW: 38,
    RIGHT_ARROW: 39,
    DOWN_ARROW: 40
};

@Component({
    template: '',
    styleUrls: ['./projectable-form.class.scss'],
    standalone: false
})
export abstract class ProjectableForm extends ExtendableComponent implements DoCheck, OnInit {
    @Input() isModal = false;
    @Output() abstract close: EventEmitter<any>;

    _formData: any = {};
    @Input() set formData(data) {
      this._formData = data;
      this.dataInit = true;
    }
    get formData(): any {
      return this._formData;
    }

    _entityId: String;
    @Input() set entityId(data: String) {
      this._entityId = data;
    }

    get entityId(): String {
      return this._entityId;
    }

    @Output() dataChange: EventEmitter<any> = new EventEmitter<any>();
    @ViewChild('nameFieldInput') nameFieldInput: ElementRef;
    @ViewChild('scrollContainer') scrollContainer: ElementRef;

    abstract clear(): void;
    abstract save(): void;

    public dataInit = false;
    public errors: any = {};
    protected entityType = 'identities';
    protected entityClass: any = Entity;
    public isLoading = false;
    protected usePreviousLocation = true;
    moreActions: any[] = [];
    tagElements: any = [];
    tagData: any = [];
    hideTags = false;
    initData: any = {};
    _dataChange = false;
    apiOptions = [{id: 'cli', label: 'Copy as CLI'}, {id: 'curl', label: 'Copy as CURL'}];
    basePath = '';
    baseHref;
    previousRoute;
    showMore = false;

    _settings: any = {};
    checkDataChangeDebounced = debounce(this.checkDataChange, 100, {maxWait: 100});
    ignoreDataChange = false;
    isScrolled = false;

    subscription: Subscription = new Subscription();

    formInit = false;

    @ViewChild('formContainer') formContainer!: ElementRef;

    protected constructor(
        protected growlerService: GrowlerService,
        @Inject(SHAREDZ_EXTENSION) protected extService: ExtensionService,
        @Inject(ZITI_DATA_SERVICE) protected zitiService: ZitiDataService,
        protected router?: Router,
        protected route?: ActivatedRoute,
        protected location?: Location,
        @Inject(SETTINGS_SERVICE) protected settingsService?: SettingsService,
        protected dialogForm?: MatDialog,
    ) {
        super();
        this.previousRoute = this.router.getCurrentNavigation().previousNavigation?.finalUrl?.toString();
        this.subscription.add(
            this.route?.params?.subscribe(params => {
                const id = params['id'];
                if (!isEmpty(id)) {
                    this.entityId = id;
                } else {
                    this.entityId = undefined;
                }
            })
        );
        this.subscription.add(
            router.events.subscribe((event: any) => {
                if (event instanceof NavigationEnd) {
                    const snapshot = this.router.routerState.snapshot.root;
                    const routeConfig = snapshot.firstChild?.routeConfig;
                    if (!routeConfig?.path) {
                      return;
                    }
                    const pathSegments = routeConfig.path.split('/');
                    this.basePath = pathSegments[0];
                }
            })
        );
        this.subscription.add(
            this.settingsService.settingsChange?.subscribe(params => {
                const newSettings = cloneDeep(this.settingsService.settings);
                if (
                    !isEmpty(this.formData?.id) && (isEmpty(this._settings) ||
                    this._settings.session?.id === newSettings.session?.id ||
                        isEmpty(newSettings.session?.id))
                ) {
                    this._settings = newSettings;
                    return;
                }
                this._settings = newSettings;
                if (!this.formInit) {
                    return;
                }
                this.ngOnInit();
            })
        );
        this.initBaseHref();
    }

    get isEdit() {
        return !isEmpty(this.formData.id);
    }

    override ngAfterViewInit(skipFocus = false) {
        super.ngAfterViewInit();
        this.errors = {};
        if (!skipFocus) {
            this.nameFieldInput?.nativeElement?.focus();
        }
        if (this.extService?.moreActions) {
            this.moreActions = [...this.moreActions, ...this.extService.moreActions];
        }
        this.formContainer?.nativeElement?.addEventListener('scroll', () => {
          this.isScrolled = this.formContainer.nativeElement.scrollTop > 0;
        });
    }

    ngOnInit() {
        if (this.entityId) {
            if (this.entityId === 'create') {
                this.formData = new this.entityClass();
                this.initData = cloneDeep(this.formData);
                this._dataChange = false;
                this.entityUpdated();
                return;
            }
            this.isLoading = true;
            this.zitiService.getSubdata(this.entityType, this.entityId, '').then((entity: any) => {
                this.formData = entity?.data || this.formData || new this.entityClass();
                this.initData = cloneDeep(this.formData);
                this._dataChange = false;
                this.entityUpdated();
            }).finally(() => {
                this.isLoading = false;
            });
        }
        this.formInit = true;
    }

    initBaseHref() {
        try {
            const base = document?.querySelector('base');
            this.baseHref = base ? base.getAttribute('href') : '/';
        } catch (error) {
            this.baseHref = '/';
        }
    }

    showMoreChanged(showMore) {
        if (!showMore || this.hideTags) {
            return;
        }
    }

    loadTags() {
        this.hideTags = localStorage.getItem("hideTags")=="yes";
        if (this.hideTags) {
            return;
        }
    }

    getTagValues() {
        return [];
    }

    closeForm(refresh = true, ignoreChanges = false, data?, event?) {
        if (!ignoreChanges && this._dataChange) {
            const confirmed = confirm('You have unsaved changes. Do you want to leave this page and discard your changes or stay on this page?');
            if (!confirmed) {
                return;
            }
        }
        if (this.isModal) {
            this.closeModal(refresh, ignoreChanges);
        } else {
            this.returnToListPage();
        }
    }

    closeModal(refresh = true, ignoreChanges = false, data?, event?): void {
        this.close.emit({refresh: refresh});
        if (event) {
            event.stopPropagation();
        }
    }

    returnToListPage() {
        if (this.location && this.previousRoute && this.usePreviousLocation) {
            this.location.back();
        } else {
            this.router?.navigateByUrl(`${this.basePath}`);
        }
    }

    ngDoCheck() {
        this.checkDataChangeDebounced();
    }

    protected checkDataChange() {
        if (this.ignoreDataChange) {
            return;
        }
        let initData = cloneDeep(this.initData);
        initData = this.omitEmptyData(initData);
        let formData = cloneDeep(this.formData);
        formData = this.omitEmptyData(formData);
        const dataChange = !isEqual(initData, formData);
        if (dataChange !== this._dataChange) {
            this.dataChange.emit(dataChange);
        }
        this._dataChange = dataChange;
    }

    omitEmptyData(object) {
        forEach(object, (val, key) => {
            if(this.omitDataIteratee(val)) {
                unset(object, key);
            } else if(isObject(val)) {
                this.omitEmptyData(val);
            }
        });
        return object;
    }

    omitDataIteratee(val) {
        return isNil(val) || (isArray(val) && isEmpty(val));
    }

    copyToClipboard(val) {
        navigator.clipboard.writeText(val);
        const growlerData = new GrowlerModel(
            'success',
            'Success',
            `Text Copied`,
            `API call URL copied to clipboard`,
        );
        this.growlerService.show(growlerData);
    }

    getRolesCLIVariable(selectedRoles) {
        let rolesVar = '';
        selectedRoles.forEach((role, index)=> {
            if (index > 0) {
                rolesVar += ',';
            }
            rolesVar += role;
        });
        return rolesVar;
    }

    getRolesCURLVariable(selectedRoles) {
        let rolesVar = '';
        selectedRoles.forEach((role, index)=> {
            if (index > 0) {
                rolesVar += ', ';
            }
            rolesVar += `"${role}"`;
        });
        rolesVar = `[${rolesVar}]`;
        return rolesVar;
    }

    canDeactivate() {
        if (this._dataChange) {
            return confirm('You have unsaved changes. Do you want to leave this page and discard your changes or stay on this page?');
        }
        return true;
    }

    protected entityUpdated() {
        //no-op
        if (this.isEdit && !this.moreActions.find(action => action.name === 'delete')) {
            this.moreActions.push({
                name: 'delete',
                label: 'Delete',
                icon: 'icon-trash',
                hidden: () => false
            });
        }
    }

    getRoleAttributes(type: string) {
        return this.zitiService.get(type, {}, []).then((results) => {
            return results.data;
        });
    }

    private deleteConfirmDialogRef: any;

    protected deleteEntity() {
        if (!this.dialogForm) {
            console.error('MatDialog not injected. Cannot show delete confirmation.');
            return;
        }

        const entityLabel = this.getEntityLabel();
        const isService = this.entityType === 'services';
        const showOrphanedEntitiesOption = isService && !isEmpty(this.settingsService?.zitiSemver);

        const data: any = {
            appendId: `Delete${this.entityType}`,
            title: 'Delete',
            message: `Are you sure you would like to delete the following ${entityLabel}?`,
            bulletList: [this.formData.name],
            confirmLabel: 'Yes',
            cancelLabel: 'Oops, no get me out of here',
            imageUrl: '../../assets/svgs/Confirm_Trash.svg',
            showCancelLink: true
        };

        if (showOrphanedEntitiesOption) {
            data.showSecondaryConfirm = true;
            data.secondaryConfirmLabel = 'Delete all orphaned associated configs and service policies?';
            data.secondaryConfirmed = false;
            data.secondaryInfoLabel = 'Choosing this option will also delete any associated configs and/or service policies that ONLY make reference to this service';
            data.secondaryActionLabel = 'Preview Deletions';
            data.confirmLabelAlt = 'Delete All';
            data.secondaryAction = () => this.openPreviewDeletions();
        }

        this.deleteConfirmDialogRef = this.dialogForm.open(ConfirmComponent, {
            data: data,
            autoFocus: false,
        });
        this.deleteConfirmDialogRef.afterClosed().subscribe((result) => {
            if (result?.confirmed) {
                this.isLoading = true;
                const deleteOrphans = result.secondaryConfirmed || false;

                let deletePromise;
                if (isService && deleteOrphans) {
                    deletePromise = this.deleteServiceWithOrphans();
                } else {
                    deletePromise = this.zitiService.delete(this.entityType, this.formData.id);
                }

                deletePromise.then(() => {
                    const growlerData = new GrowlerModel(
                        'success',
                        'Success',
                        `${entityLabel} Deleted`,
                        `Successfully deleted ${entityLabel.toLowerCase()} "${this.formData.name}"`,
                    );
                    this.growlerService.show(growlerData);
                    this.returnToListPage();
                }).catch((error) => {
                    const growlerData = new GrowlerModel(
                        'error',
                        'Error',
                        `Delete Failed`,
                        `Failed to delete ${entityLabel.toLowerCase()}: ${error?.error?.error?.message || error?.message || 'Unknown error'}`,
                    );
                    this.growlerService.show(growlerData);
                }).finally(() => {
                    this.isLoading = false;
                });
            }
        });
    }

    protected deleteServiceWithOrphans(): Promise<any> {
        return Promise.reject('deleteServiceWithOrphans must be overridden in child class');
    }

    protected openPreviewDeletions() {
        // This should be overridden by child classes that support preview
        console.log('Preview deletions not implemented for this entity type');
    }

    protected getEntityLabel(): string {
        // Convert entity type to a readable label
        // e.g., "external-jwt-signers" -> "JWT Signer"
        const typeMap: { [key: string]: string } = {
            'external-jwt-signers': 'JWT Signer',
            'identities': 'Identity',
            'services': 'Service',
            'edge-routers': 'Edge Router',
            'transit-routers': 'Transit Router',
            'edge-router-policies': 'Edge Router Policy',
            'service-edge-router-policies': 'Service Edge Router Policy',
            'service-policies': 'Service Policy',
            'configs': 'Configuration',
            'config-types': 'Config Type',
            'posture-checks': 'Posture Check',
            'auth-policies': 'Auth Policy',
            'cas': 'Certificate Authority'
        };
        return typeMap[this.entityType] || this.entityType;
    }
}
